import axios from 'axios';

// Create Axios instance following existing patterns
const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/notifications`,
  withCredentials: true,
});

// Attach token from localStorage/sessionStorage if present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Get user notifications with filtering and pagination
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 1)
 * @param {number} params.limit - Items per page (default: 20)
 * @param {boolean} params.onlyUnread - Filter only unread notifications
 * @param {string} params.types - Comma-separated notification types
 * @param {string} params.module - Filter by module (ASSESSMENT, OKR, FEEDBACK, etc.)
 * @returns {Promise<Object>} API response with notifications and pagination
 */
export const getNotifications = async (params = {}) => {
  const response = await api.get('/', { params });
  return response.data;
};

/**
 * Get unread notification count
 * @returns {Promise<Object>} API response with unread count
 */
export const getUnreadCount = async () => {
  const response = await api.get('/unread-count');
  return response.data;
};

/**
 * Get notification statistics
 * @returns {Promise<Object>} API response with notification stats
 */
export const getNotificationStats = async () => {
  const response = await api.get('/stats');
  return response.data;
};

// =====================
// NOTIFICATION PREFERENCES API
// =====================

/**
 * Get user notification preferences
 * @returns {Promise<Object>} API response with preferences
 */
export const getNotificationPreferences = async () => {
  const response = await api.get('/preferences');
  return response.data;
};

/**
 * Update notification preference for a specific type
 * @param {string} notificationType - Type of notification (ASSESSMENT, OKR, etc.)
 * @param {Object} updates - Preference updates
 * @returns {Promise<Object>} API response with updated preference
 */
export const updateNotificationPreference = async (notificationType, updates) => {
  const response = await api.put(`/preferences/${notificationType}`, updates);
  return response.data;
};

/**
 * Bulk update notification preferences
 * @param {Array} preferences - Array of preference updates
 * @returns {Promise<Object>} API response with updated preferences
 */
export const bulkUpdateNotificationPreferences = async (preferences) => {
  const response = await api.put('/preferences', { preferences });
  return response.data;
};

/**
 * Reset user preferences to defaults
 * @returns {Promise<Object>} API response with reset preferences
 */
export const resetNotificationPreferences = async () => {
  const response = await api.post('/preferences/reset');
  return response.data;
};

/**
 * Get notification preference statistics
 * @returns {Promise<Object>} API response with preference stats
 */
export const getNotificationPreferenceStats = async () => {
  const response = await api.get('/preferences/stats');
  return response.data;
};

/**
 * Get digest notifications
 * @param {string} frequency - Digest frequency (DAILY or WEEKLY)
 * @returns {Promise<Object>} API response with digest data
 */
export const getNotificationDigest = async (frequency) => {
  const response = await api.get(`/digest/${frequency}`);
  return response.data;
};

// =====================
// NOTIFICATION ANALYTICS API
// =====================

/**
 * Track notification event
 * @param {string} notificationId - Notification ID
 * @param {string} eventType - Event type (READ, CLICKED, DISMISSED)
 * @param {Object} options - Additional options
 * @param {string} options.channel - Channel (IN_APP, EMAIL, WEBSOCKET)
 * @param {Object} options.metadata - Additional metadata
 * @returns {Promise<Object>} API response
 */
export const trackNotificationEvent = async (notificationId, eventType, options = {}) => {
  const { channel = 'IN_APP', metadata = {} } = options;
  const response = await api.post(`/${notificationId}/track/${eventType}`, {
    channel,
    metadata,
  });
  return response.data;
};

/**
 * Get analytics for a specific notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} API response with analytics data
 */
export const getNotificationAnalytics = async (notificationId) => {
  const response = await api.get(`/${notificationId}/analytics`);
  return response.data;
};

/**
 * Get user notification analytics
 * @param {Object} options - Query options
 * @param {string} options.startDate - Start date (ISO string)
 * @param {string} options.endDate - End date (ISO string)
 * @param {Array} options.eventTypes - Event types to include
 * @param {Array} options.channels - Channels to include
 * @returns {Promise<Object>} API response with user analytics
 */
export const getUserNotificationAnalytics = async (options = {}) => {
  const params = {};
  if (options.startDate) params.startDate = options.startDate;
  if (options.endDate) params.endDate = options.endDate;
  if (options.eventTypes) params.eventTypes = options.eventTypes.join(',');
  if (options.channels) params.channels = options.channels.join(',');

  const response = await api.get('/analytics/user', { params });
  return response.data;
};

/**
 * Get system-wide notification analytics (admin only)
 * @param {Object} options - Query options
 * @param {string} options.startDate - Start date (ISO string)
 * @param {string} options.endDate - End date (ISO string)
 * @param {string} options.groupBy - Grouping option (day, week, month)
 * @returns {Promise<Object>} API response with system analytics
 */
export const getSystemNotificationAnalytics = async (options = {}) => {
  const params = {};
  if (options.startDate) params.startDate = options.startDate;
  if (options.endDate) params.endDate = options.endDate;
  if (options.groupBy) params.groupBy = options.groupBy;

  const response = await api.get('/analytics/system', { params });
  return response.data;
};

/**
 * Get top performing notifications (admin only)
 * @param {Object} options - Query options
 * @param {number} options.limit - Number of results to return
 * @param {string} options.startDate - Start date (ISO string)
 * @param {string} options.endDate - End date (ISO string)
 * @param {string} options.sortBy - Sort by metric (readRate, clickRate, engagementRate)
 * @returns {Promise<Object>} API response with top notifications
 */
export const getTopPerformingNotifications = async (options = {}) => {
  const params = {};
  if (options.limit) params.limit = options.limit;
  if (options.startDate) params.startDate = options.startDate;
  if (options.endDate) params.endDate = options.endDate;
  if (options.sortBy) params.sortBy = options.sortBy;

  const response = await api.get('/analytics/top-performing', { params });
  return response.data;
};

/**
 * Mark specific notifications as read
 * @param {Array<string>} notificationIds - Array of notification IDs
 * @returns {Promise<Object>} API response with update results
 */
export const markNotificationsAsRead = async (notificationIds) => {
  const response = await api.post('/mark-read', { notificationIds });
  return response.data;
};

/**
 * Mark a single notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} API response with update result
 */
export const markNotificationAsRead = async (notificationId) => {
  return markNotificationsAsRead([notificationId]);
};

/**
 * Mark all user notifications as read
 * @returns {Promise<Object>} API response with update count
 */
export const markAllNotificationsAsRead = async () => {
  const response = await api.post('/mark-all-read');
  return response.data;
};

/**
 * Delete specific notifications
 * @param {Array<string>} notificationIds - Array of notification IDs
 * @returns {Promise<Object>} API response with deletion results
 */
export const deleteNotifications = async (notificationIds) => {
  const response = await api.delete('/', { data: { notificationIds } });
  return response.data;
};

/**
 * Delete a single notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} API response with deletion result
 */
export const deleteNotification = async (notificationId) => {
  const response = await api.delete(`/${notificationId}`);
  return response.data;
};

/**
 * Get a specific notification by ID
 * @param {string} notificationId - Notification ID
 * @returns {Promise<Object>} API response with notification data
 */
export const getNotificationById = async (notificationId) => {
  const response = await api.get(`/${notificationId}`);
  return response.data;
};

/**
 * Create a notification (admin only)
 * @param {Object} notificationData - Notification data
 * @param {string} notificationData.userId - Target user ID
 * @param {string} notificationData.title - Notification title
 * @param {string} notificationData.message - Notification message
 * @param {string} notificationData.type - Notification type (INFO, SUCCESS, WARNING, ERROR)
 * @param {Object} notificationData.data - Additional data
 * @returns {Promise<Object>} API response with created notification
 */
export const createNotification = async (notificationData) => {
  const response = await api.post('/', notificationData);
  return response.data;
};

/**
 * Broadcast notification to multiple users (admin only)
 * @param {Object} broadcastData - Broadcast data
 * @param {Array<string>} broadcastData.userIds - Array of user IDs
 * @param {string} broadcastData.title - Notification title
 * @param {string} broadcastData.message - Notification message
 * @param {string} broadcastData.type - Notification type
 * @param {Object} broadcastData.data - Additional data
 * @returns {Promise<Object>} API response with created notifications
 */
export const broadcastNotification = async (broadcastData) => {
  const response = await api.post('/broadcast', broadcastData);
  return response.data;
};

// Notification polling service for real-time updates
class NotificationPoller {
  constructor(callback, interval = 30000) { // Poll every 30 seconds by default
    this.callback = callback;
    this.interval = interval;
    this.timeoutId = null;
    this.lastUnreadCount = 0;
  }

  start() {
    this.poll();
  }

  stop() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  async poll() {
    try {
      const response = await getUnreadCount();
      const currentUnreadCount = response.data.count;

      // Only trigger callback if count changed
      if (currentUnreadCount !== this.lastUnreadCount) {
        this.lastUnreadCount = currentUnreadCount;
        if (this.callback) {
          this.callback(currentUnreadCount);
        }
      }
    } catch (error) {
      console.error('Error polling notifications:', error);
    }

    // Schedule next poll
    this.timeoutId = setTimeout(() => this.poll(), this.interval);
  }

  setInterval(newInterval) {
    this.interval = newInterval;
  }
}

// Export notification types for filtering
export const NOTIFICATION_TYPES = {
  INFO: 'INFO',
  SUCCESS: 'SUCCESS',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
};

// Export notification modules for filtering
export const NOTIFICATION_MODULES = {
  ASSESSMENT: 'ASSESSMENT',
  OKR: 'OKR',
  FEEDBACK: 'FEEDBACK',
  MEETING: 'MEETING',
  SYSTEM: 'SYSTEM',
};

// Helper functions for notification management
export const notificationHelpers = {
  /**
   * Group notifications by date
   * @param {Array<Object>} notifications - Array of notifications
   * @returns {Object} Grouped notifications by date
   */
  groupByDate: (notifications) => {
    const grouped = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    notifications.forEach((notification) => {
      const notificationDate = new Date(notification.createdAt);
      let dateKey;

      if (notificationDate.toDateString() === today.toDateString()) {
        dateKey = 'Hoje';
      } else if (notificationDate.toDateString() === yesterday.toDateString()) {
        dateKey = 'Ontem';
      } else {
        dateKey = notificationDate.toLocaleDateString('pt-BR', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      }

      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(notification);
    });

    return grouped;
  },

  /**
   * Get notification icon based on type
   * @param {string} type - Notification type
   * @returns {string} Icon emoji or character
   */
  getIcon: (type) => {
    const icons = {
      INFO: '📢',
      SUCCESS: '✅',
      WARNING: '⚠️',
      ERROR: '❌',
    };
    return icons[type] || '📢';
  },

  /**
   * Get notification priority based on type and data
   * @param {Object} notification - Notification object
   * @returns {string} Priority level (high, medium, low)
   */
  getPriority: (notification) => {
    if (notification.type === 'ERROR') return 'high';
    if (notification.type === 'WARNING') return 'medium';
    if (notification.data?.priority) return notification.data.priority.toLowerCase();
    return 'low';
  },

  /**
   * Format notification time
   * @param {string} timestamp - ISO timestamp
   * @returns {string} Formatted time string
   */
  formatTime: (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / 60000);

    if (diffInMinutes < 1) return 'Agora';
    if (diffInMinutes < 60) return `${diffInMinutes}min atrás`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h atrás`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d atrás`;

    return date.toLocaleDateString('pt-BR');
  },
};

// Export the poller for real-time notifications
export { NotificationPoller };